import React from "react";
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";

const headerCellSx = {
  fontWeight: 700, fontSize: "0.7rem", color: "#666",
  py: 0.5, bgcolor: "#f5f5f5",
};

const bodyCellSx = { fontSize: "0.75rem", py: 0.4, px: 1 };

const RESUMEN_COLORS = {
  activo:           "#c8e6c9",
  vendido_activo:   "#fff9c4",
  vendido:          "#ffccbc",
  mantenimiento:    "#fff9c4",
  soft_activo:      "#e1f5fe",
  soft_vendido_activo: "#f3e5f5",
};

const RESUMEN_LABELS = {
  activo:              "Activos",
  vendido_activo:      "Vendido Activo",
  vendido:             "Vendido Inactivo",
  mantenimiento:       "Mantenimiento",
  soft_activo:         "Soft Activo",
  soft_vendido_activo: "Soft Vendido Activo",
};

function GrupoTable({ title, rows }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, color: "#00796b", textTransform: "uppercase",
              display: "block", mb: 0.5, pl: 0.5 }}
      >
        {title}
      </Typography>
      <TableContainer component={Paper} elevation={0}
        sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <Table size="small" padding="none">
          <TableHead>
            <TableRow>
              {["Cantidad", "Modelo", "Precio", "Ciudad"].map((h) => (
                <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{r.cantidad}</TableCell>
                <TableCell sx={bodyCellSx}>{r.modelo || "—"}</TableCell>
                <TableCell sx={bodyCellSx}>
                  {r.precio === null || r.precio === "" ? "—" : r.precio}
                </TableCell>
                <TableCell sx={bodyCellSx}>{r.ciudad}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function ResumenTable({ equipos }) {
  const rows = (equipos || [])
    .filter((eq) => eq?.resumen)
    .map((eq) => ({ resumen: eq.resumen, tipo: eq.tipo || "Equipo" }));

  const keys = ["activo", "vendido_activo", "vendido", "mantenimiento"];

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 800, color: "#00796b", textTransform: "uppercase",
              display: "block", mb: 0.5, pl: 0.5 }}
      >
        Resumen de Ventas
      </Typography>
      <TableContainer component={Paper} elevation={0}
        sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <Table size="small" padding="none">
          <TableBody>
            {rows.map(({ resumen, tipo }) =>
              keys.map((key) => (
                <TableRow key={`${tipo}-${key}`}>
                  <TableCell sx={{
                    ...bodyCellSx, fontWeight: 700, width: 60,
                    bgcolor: RESUMEN_COLORS[key],
                  }}>
                    {resumen?.[key] ?? 0}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, width: 80, bgcolor: RESUMEN_COLORS[key] }}>
                    {tipo}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, bgcolor: RESUMEN_COLORS[key] }}>
                    {RESUMEN_LABELS[key]}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function ResumenEquipo({ data, onClear }) {
  if (!data) return null;
  const equipos = Array.isArray(data.equipos)
    ? data.equipos
    : [data.equipo1, data.equipo2, data.equipo3].filter(Boolean);

  return (
    <Paper elevation={0} sx={{ mt: 1, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{
        px: 1.5, py: 0.8, display: "flex", alignItems: "center",
        justifyContent: "space-between", bgcolor: "#fcfcfc", borderBottom: "1px solid #eee",
      }}>
        <Typography variant="caption"
          sx={{ fontWeight: 800, color: "#333", textTransform: "uppercase" }}>
          Resumen
        </Typography>
        {onClear && (
          <Typography
            variant="caption"
            onClick={onClear}
            sx={{ cursor: "pointer", color: "primary.main", fontSize: "0.7rem" }}
          >
            Ocultar
          </Typography>
        )}
      </Box>

      {/* Contenido */}
      <Box sx={{ p: 1.5, display: "flex", gap: 2, flexWrap: "wrap" }}>
        {/* Izquierda: tablas de grupos */}
        <Box sx={{ flex: 2, minWidth: 280 }}>
          {equipos.map((equipo, index) => (
            <GrupoTable
              key={`${equipo?.tipo || "equipo"}-${index}`}
              title={equipo?.tipo || "Equipo"}
              rows={equipo?.grupo ?? []}
            />
          ))}
        </Box>

        {/* Derecha: resumen */}
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <ResumenTable equipos={equipos} />
        </Box>
      </Box>
    </Paper>
  );
}
